import React, { useState } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    TextField,
    IconButton,
    InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../context/AppContext";
import { sleep, storeToken } from "../utils/utilFunctions";
import { loginUser } from "../api/users";

const Login = () => {
    const theme = useTheme();
    const [showPassword, setShowPassword] = useState(false);
    const [form, setForm] = useState({ email: "", password: "" });
    const [errors, setErrors] = useState({ email: "", password: "" });
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { setIsLoading } = useAppContext();

    const handleChange = (e) => {
        setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const isValidEmail = (email) =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

    const handleLogin = async () => {
        setIsLoading(true);

        let newErrors = { email: "", password: "" };

        if (!form.email.trim()) {
            newErrors.email = t("login.errors.requiredEmail");
        } else if (!isValidEmail(form.email)) {
            newErrors.email = t("login.errors.invalidEmail");
        }

        if (!form.password.trim()) {
            newErrors.password = t("login.errors.requiredPassword");
        }

        const hasErrors = newErrors.email || newErrors.password;
        setErrors(newErrors);

        if (hasErrors) {
            setIsLoading(false);
            return;
        }

        try {
            await loginUser(form.email, form.password);
            navigate('/');
        } catch (error) {
            console.error("Login failed:", error);
            if (error.message === "email_not_validated") {
                setErrors({
                    email: t("errors.email_not_validated"),
                    password: ""
                });
            } else {
                setErrors({
                    email: t("login.errors.invalidCredentials"),
                    password: t("login.errors.invalidCredentials"),
                });
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                bgcolor: theme.palette.background.default,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 2,
            }}
        >
            <Card
                sx={{
                    maxWidth: 400,
                    width: "100%",
                    bgcolor: theme.palette.sidebar?.background || theme.palette.background.paper,
                    boxShadow: 6,
                    borderRadius: 2,
                }}
            >
                <CardContent>
                    <Typography variant="h5" fontWeight="bold" mb={3} textAlign="center">
                        {t("login.title")}
                    </Typography>

                    <form onSubmit={(e) => {
                        e.preventDefault(); // prevent default form submission
                        handleLogin();      // trigger login
                    }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            label={t("login.email")}
                            name="email"
                            value={form.email}
                            onChange={handleChange}
                            error={!!errors.email}
                            helperText={errors.email}
                            sx={{ mb: 2 }}
                            InputLabelProps={{ style: { color: theme.palette.text.primary } }}
                            InputProps={{
                                style: { color: theme.palette.text.primary },
                            }}
                        />

                        <TextField
                            fullWidth
                            variant="outlined"
                            label={t("login.password")}
                            name="password"
                            type={showPassword ? "text" : "password"}
                            value={form.password}
                            onChange={handleChange}
                            error={!!errors.password}
                            helperText={errors.password}
                            sx={{ mb: 3 }}
                            InputLabelProps={{ style: { color: theme.palette.text.primary } }}
                            InputProps={{
                                style: { color: theme.palette.text.primary },
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword((prev) => !prev)}
                                            edge="end"
                                            sx={{ color: theme.palette.text.primary }}
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            size="large"
                            type="submit"
                            sx={{
                                fontWeight: 600,
                                textTransform: "none",
                                borderRadius: 2,
                            }}
                        >
                            {t("login.loginButton")}
                        </Button>
                        <Button
                            fullWidth
                            variant="outlined"
                            color="secondary"
                            size="large"
                            onClick={() => navigate('/register')}
                            sx={{
                                fontWeight: 600,
                                textTransform: "none",
                                borderRadius: 2,
                                mt: 1,
                            }}
                        >
                            {t("register.submit")}
                        </Button>
                        <Typography
                            variant="body2"
                            sx={{
                                color: theme.palette.primary.main,
                                textAlign: "right",
                                mb: 2,
                                cursor: "pointer",
                                fontWeight: 500,
                                "&:hover": {
                                    textDecoration: "underline",
                                },
                            }}
                            onClick={() => {
                                // You can navigate or trigger something here
                                navigate('/forgotPassword')
                            }}
                        >
                            {t("login.forgotPassword")}
                        </Typography>
                    </form>
                </CardContent>
            </Card>
        </Box>
    );
};

export default Login;